/**
 * ECharts Option Builder — Heatmap chart.
 */
import type { EChartsOption } from 'echarts';
import { type ChartTheme, buildBaseOption, tooltipConfig, xAxisConfig, yAxisConfig, gridConfig } from '../theme';
import { formatChartValue } from '../../../../utils/formatters';
import type { ChartData } from '../../../../api/types';
import { colIdx, colType, uniqueValues, formatAxisLabel } from './utils';

export function buildHeatmapChart(
  data: ChartData,
  theme: ChartTheme,
  animated: boolean,
): EChartsOption {
  const meta = data.meta;
  // Heatmap: x = first dimension, series = second dimension (Y axis), y[0] = value
  // This matches the standard meta convention from _build_scatter_meta:
  //   y[] = numeric measure, series = categorical dimension
  const xCol = meta?.x ?? data.columns[0].name;
  const yCol = meta?.series ?? data.columns[1]?.name ?? data.columns[0].name;
  const valueCol = meta?.y?.[0] ?? data.columns[2]?.name ?? data.columns[0].name;

  const xIdx = colIdx(data, xCol);
  const yIdx = colIdx(data, yCol);
  const vIdx = colIdx(data, valueCol);

  const xType = colType(data, xCol);
  const yType = colType(data, yCol);
  const xCategories = uniqueValues(data, xCol).map((v) => formatAxisLabel(v, xType));
  const yCategories = uniqueValues(data, yCol).map((v) => formatAxisLabel(v, yType));

  // Build lookup from raw value → category index
  const rawXValues = uniqueValues(data, xCol);
  const rawYValues = uniqueValues(data, yCol);
  const xMap = new Map(rawXValues.map((v, i) => [v, i]));
  const yMap = new Map(rawYValues.map((v, i) => [v, i]));

  let minVal = Infinity;
  let maxVal = -Infinity;

  const heatmapData = data.rows.map((row) => {
    const xv = xMap.get(row[xIdx]) ?? 0;
    const yv = yMap.get(row[yIdx]) ?? 0;
    const val = row[vIdx] ?? 0;
    if (val < minVal) minVal = val;
    if (val > maxVal) maxVal = val;
    return [xv, yv, val];
  });

  const borderColor = theme.mode === 'dark' ? '#1f2937' : '#ffffff';

  return {
    ...buildBaseOption(theme, animated),
    grid: { ...gridConfig(), top: 28, bottom: 80 },
    tooltip: {
      ...tooltipConfig(theme, 'item'),
      formatter: (params: any) => {
        const p = Array.isArray(params) ? params[0] : params;
        const [xi, yi, v] = p.data;
        return `<b>${xCategories[xi]}</b> / <b>${yCategories[yi]}</b><br/>Value: <b>${formatChartValue(v)}</b>`;
      },
    },
    legend: { show: false },
    xAxis: {
      ...xAxisConfig(theme),
      data: xCategories,
      splitArea: { show: true },
      axisLabel: { ...xAxisConfig(theme).axisLabel, fontSize: 10, rotate: 30 },
    },
    yAxis: {
      ...yAxisConfig(theme),
      type: 'category' as const,
      data: yCategories,
      splitLine: { show: false },
      splitArea: { show: true },
      axisLabel: { ...yAxisConfig(theme).axisLabel, fontSize: 10, interval: 2 },
    },
    visualMap: {
      min: minVal === Infinity ? 0 : minVal,
      max: maxVal === -Infinity ? 1 : maxVal,
      calculable: true,
      orient: 'horizontal' as const,
      left: 'center',
      bottom: 0,
      textStyle: { color: theme.textColor },
    },
    series: [
      {
        type: 'heatmap',
        data: heatmapData,
        itemStyle: {
          borderWidth: 0.3,
          borderColor,
        },
        label: { show: false },
        emphasis: { focus: 'none' as const },
      },
    ],
  };
}